import { get, set } from "../../../../bpmn.io/min-dash";
import { html } from 'htm/preact';

/*
 * Import components and utilities from our extension API. Warning: for demo experiments only.
 */

/*
 * This is a custom properties provider for the properties panel.
 * It adds a new group `Range` with range specific properties.
 */

import { CheckboxEntry, TextFieldEntry, CreateIcon, DeleteIcon } from "../../../../bpmn.io/@bpmn-io/properties-panel";

function PasswordCheckboxEntry(field, editField) {
  return {
    id: "isPassword",
    component: PasswordEntry,
    field,
    isEdited: (field) => !!field.isPassword,
    getValue: () => field.isPassword || false,
    onChange: (value) => editField(field, ["isPassword"], value),
  };
}

function PasswordEntry(props) {
  const { id, field, getValue, onChange } = props;

  const debounce = (fn) => fn;

  return CheckboxEntry({
    debounce,
    id,
    element: field,
    label: "¿Es una contraseña?",
    getValue,
    setValue: onChange,
  });
}

function AutocompleteCheckboxEntry(field, editField) {
  return {
    id: "isAutocomplete",
    component: AutocompleteEntry,
    field,
    isEdited: (field) => !!field.isAutocomplete,
    getValue: () => field.isAutocomplete || false,
    onChange: (value) => editField(field, ["isAutocomplete"], value),
  };
}

function AutocompleteEntry(props) {
  const { id, field, getValue, onChange } = props;

  const debounce = (fn) => fn;

  return CheckboxEntry({
    debounce,
    id,
    element: field,
    label: "¿Autocompletar?",
    getValue,
    setValue: onChange,
  });
}

function FileInputRequiredEntry(field, editField) {
  return {
    id: 'fileInput-required',
    component: FileInputRequiredCheckbox,
    field,
    isEdited: (f) => !!(f.validate && f.validate.required),
    getValue: () => !!(field.validate && field.validate.required),
    onChange: (value) => {
      const currentValidate = field.validate || {};
      editField(field, ['validate'], { ...currentValidate, required: value || undefined });
    },
  };
}

function FileInputRequiredCheckbox(props) {
  const { id, field, getValue, onChange } = props;
  const debounce = (fn) => fn;
  return CheckboxEntry({
    debounce,
    id,
    element: field,
    label: 'Requerido',
    getValue,
    setValue: onChange,
  });
}

function CaptureContentAsVariableEntry(field, editField) {
  return {
    id: "captureContentAsVariable",
    component: CaptureContentAsVariableCheckbox,
    field,
    isEdited: (field) => !!field.captureContentAsVariable,
    getValue: () => field.captureContentAsVariable || false,
    onChange: (value) => editField(field, ["captureContentAsVariable"], value),
  };
}

function CaptureContentAsVariableCheckbox(props) {
  const { id, field, getValue, onChange } = props;

  const debounce = (fn) => fn;

  return CheckboxEntry({
    debounce,
    id,
    element: field,
    label: "Capturar contenido como variable Base64",
    description: "Habilita la captura del contenido del archivo en base64 como variable de flujo",
    getValue,
    setValue: onChange,
  });
}

function VariableNameEntry(field, editField) {
  return {
    id: "variableName",
    component: VariableNameField,
    field,
    isEdited: (field) => !!field.variableName,
    getValue: () => field.variableName || '',
    onChange: (value) => editField(field, ["variableName"], value),
  };
}

function VariableNameField(props) {
  const { id, field, getValue, onChange } = props;

  const debounce = (fn) => fn;

  // Solo mostrar si está habilitada la captura de contenido
  if (!field.captureContentAsVariable) {
    return null;
  }

  return TextFieldEntry({
    debounce,
    id,
    element: field,
    label: "Nombre de variable de flujo Base64",
    description: "Nombre de la variable donde se almacenará el contenido del archivo en base64",
    getValue,
    setValue: onChange,
  });
}

function CaptureContentAsVariableEntryURL(field, editField) {
  return {
    id: "captureContentAsVariableURL",
    component: CaptureContentAsVariableCheckboxURL,
    field,
    isEdited: (field) => !!field.captureContentAsVariableURL,
    getValue: () => field.captureContentAsVariableURL || false,
    onChange: (value) => editField(field, ["captureContentAsVariableURL"], value),
  };
}

function CaptureContentAsVariableCheckboxURL(props) {
  const { id, field, getValue, onChange } = props;

  const debounce = (fn) => fn;

  return CheckboxEntry({
    debounce,
    id,
    element: field,
    label: "Capturar contenido como variable URL",
    description: "Habilita la captura de la URL como variable de flujo",
    getValue,
    setValue: onChange,
  });
}

function VariableNameEntryURL(field, editField) {
  return {
    id: "variableNameURL",
    component: VariableNameFieldURL,
    field,
    isEdited: (field) => !!field.variableNameURL,
    getValue: () => field.variableNameURL || '',
    onChange: (value) => editField(field, ["variableNameURL"], value),
  };
}

function VariableNameFieldURL(props) {
  const { id, field, getValue, onChange } = props;

  const debounce = (fn) => fn;

  // Solo mostrar si está habilitada la captura de contenido
  if (!field.captureContentAsVariableURL) {
    return null;
  }

  return TextFieldEntry({
    debounce,
    id,
    element: field,
    label: "Nombre de variable de flujo URL",
    description: "Nombre de la variable donde se almacenará la URL del archivo",
    getValue,
    setValue: onChange,
  });
}

// ── Ver archivo: entradas de panel ──────────────────────────────────────────────

function EtiquetaArchivoEntry(field, editField) {
  return {
    id: "etiquetaArchivo",
    component: EtiquetaArchivoField,
    field,
    isEdited: (f) => !!f.label,
    getValue: () => field.label || 'Ver archivo',
    onChange: (value) => editField(field, ["label"], value),
  };
}

function EtiquetaArchivoField(props) {
  const { id, field, getValue, onChange } = props;
  const debounce = (fn) => fn;
  return TextFieldEntry({
    debounce,
    id,
    element: field,
    label: "Etiqueta del texto",
    description: "Texto del label que se muestra en el componente",
    getValue,
    setValue: onChange,
  });
}

function LlaveArchivoEntry(field, editField) {
  return {
    id: "llaveArchivo",
    component: LlaveArchivoField,
    field,
    isEdited: (f) => !!f.llave,
    getValue: () => field.llave || '',
    onChange: (value) => editField(field, ["llave"], value),
  };
}

function LlaveArchivoField(props) {
  const { id, field, getValue, onChange } = props;
  const debounce = (fn) => fn;
  return TextFieldEntry({
    debounce,
    id,
    element: field,
    label: "Llave",
    description: "Ruta a la variable que contiene el ID del archivo (ej: flujo.archivo)",
    getValue,
    setValue: onChange,
  });
}

// ── Buscador y Cascada: entradas del panel de propiedades ──────────────────────

function BuscadorCheckboxEntry(field, editField) {
  return {
    id: 'buscador',
    component: BuscadorCheckbox,
    field,
    isEdited: (f) => !!f.buscador,
    getValue: () => field.buscador || false,
    onChange: (value) => editField(field, ['buscador'], value),
  };
}

function BuscadorCheckbox(props) {
  const { id, field, getValue, onChange } = props;
  const debounce = (fn) => fn;
  return CheckboxEntry({
    debounce,
    id,
    element: field,
    label: 'Buscador',
    description: 'Permite buscar y filtrar opciones escribiendo en el campo',
    getValue,
    setValue: onChange,
  });
}

function CascadaCheckboxEntry(field, editField) {
  return {
    id: 'cascada',
    component: CascadaCheckbox,
    field,
    isEdited: (f) => !!f.cascada,
    getValue: () => field.cascada || false,
    onChange: (value) => editField(field, ['cascada'], value),
  };
}

function CascadaCheckbox(props) {
  const { id, field, getValue, onChange } = props;
  const debounce = (fn) => fn;
  return CheckboxEntry({
    debounce,
    id,
    element: field,
    label: 'Cascada',
    description: 'Habilita el filtrado en cascada basado en otro campo de selección',
    getValue,
    setValue: onChange,
  });
}

function LlaveCascadaEntry(field, editField) {
  return {
    id: 'llaveCascada',
    component: LlaveCascadaField,
    field,
    isEdited: (f) => !!f.llaveCascada,
    getValue: () => field.llaveCascada || '',
    onChange: (value) => editField(field, ['llaveCascada'], value),
  };
}

function LlaveCascadaField(props) {
  const { id, field, getValue, onChange } = props;
  const debounce = (fn) => fn;

  if (!field.cascada) return null;

  return TextFieldEntry({
    debounce,
    id,
    element: field,
    label: 'Llave cascada',
    description: 'Clave del campo selección padre del que depende este campo',
    getValue,
    setValue: onChange,
  });
}

function ValorCascadaOptionEntry(editField, field, index) {
  return {
    id: 'staticOptions-' + index + '-valorCascada',
    component: ValorCascadaOptionField,
    editField,
    field,
    index,
  };
}

function ValorCascadaOptionField(props) {
  const { id, field, editField, index } = props;
  const debounce = (fn) => fn;

  return TextFieldEntry({
    debounce,
    id,
    element: field,
    label: 'Valor cascada',
    description: 'Valor del campo padre al que corresponde esta opción',
    getValue: () => {
      const values = get(field, ['values']) || [];
      return (values[index] && values[index].valorCascada) || '';
    },
    setValue: (value, error) => {
      if (error) return;
      const currentValues = get(field, ['values']) || [];
      const newValues = currentValues.map((v, i) =>
        i === index ? { ...v, valorCascada: value || undefined } : v
      );
      editField(field, ['values'], newValues);
    },
  });
}

// ── fin Selección en cascada ──────────────────────────────────────────────────

// ── Tabla Expandible: entradas del panel de propiedades ─────────────────────

function DefaultValueEntryET(props) {
  const { field, editField, id } = props;
  const debounce = fn => fn;
  return TextFieldEntry({
    debounce, element: field,
    getValue: () => field.defaultValue || '',
    id, label: 'Valor por defecto',
    description: 'Use @nombreVariable para vincular a una variable del proceso',
    setValue: (value) => editField(field, ['defaultValue'], value || undefined),
  });
}

function AllowAddEntry(props) {
  const { field, editField, id } = props;
  const debounce = fn => fn;
  return CheckboxEntry({
    debounce, id, element: field,
    label: 'Permitir agregar registros',
    getValue: () => field.allowAdd !== false,
    setValue: (v) => editField(field, ['allowAdd'], v),
  });
}

function AllowEditEntry(props) {
  const { field, editField, id } = props;
  const debounce = fn => fn;
  return CheckboxEntry({
    debounce, id, element: field,
    label: 'Permitir editar / eliminar registros',
    getValue: () => field.allowEdit !== false,
    setValue: (v) => editField(field, ['allowEdit'], v),
  });
}

function AllowCheckboxEntry(props) {
  const { field, editField, id } = props;
  const debounce = fn => fn;
  return CheckboxEntry({
    debounce, id, element: field,
    label: 'Permitir marcar/desmarcar checkbox',
    description: 'Si está desactivado, el checkbox de selección de fila es solo lectura',
    getValue: () => field.allowCheckbox !== false,
    setValue: (v) => editField(field, ['allowCheckbox'], v),
  });
}

function CountKeyEntry(props) {
  const { field, editField, id } = props;
  const debounce = fn => fn;
  return TextFieldEntry({
    debounce, element: field,
    getValue: () => field.countKey || '',
    id, label: 'Clave — cantidad de registros',
    description: 'Nombre de la variable donde se guarda el total de filas',
    setValue: (value) => editField(field, ['countKey'], value || undefined),
  });
}

function CountCheckedKeyEntry(props) {
  const { field, editField, id } = props;
  const debounce = fn => fn;
  return TextFieldEntry({
    debounce, element: field,
    getValue: () => field.countCheckedKey || '',
    id, label: 'Clave — cantidad de registros marcados',
    description: 'Nombre de la variable donde se guarda el total de filas con checkbox marcado',
    setValue: (value) => editField(field, ['countCheckedKey'], value || undefined),
  });
}

// ── Tabla Expandible: editor de columnas ────────────────────────────────────
// Componente sin estado local (stateless): toda la mutación pasa por editField,
// que dispara un re-render completo del panel de propiedades.
function ColumnsEditor(props) {
  const { field, editField } = props;
  const cols = Array.isArray(field.columns) ? field.columns : [];

  const addColumn = () => {
    editField(field, ['columns'], [
      ...cols,
      { label: 'Columna', key: 'campo' + (cols.length + 1), type: 'text', visibleInTable: true },
    ]);
  };

  const removeColumn = (index) => {
    editField(field, ['columns'], cols.filter((_, i) => i !== index));
  };

  const updateColumn = (index, key, value) => {
    editField(field, ['columns'], cols.map((c, i) => i === index ? Object.assign({}, c, { [key]: value }) : c));
  };

  // ── Opciones estáticas de una columna tipo 'select' ────────────────────────
  const addColumnOption = (index) => {
    const col = cols[index];
    const values = Array.isArray(col.values) ? col.values : [];
    updateColumn(index, 'values', [...values, { label: 'Opción', value: 'valor' + (values.length + 1) }]);
  };

  const removeColumnOption = (index, optIndex) => {
    const col = cols[index];
    const values = Array.isArray(col.values) ? col.values : [];
    updateColumn(index, 'values', values.filter((_, i) => i !== optIndex));
  };

  const updateColumnOption = (index, optIndex, key, value) => {
    const col = cols[index];
    const values = Array.isArray(col.values) ? col.values : [];
    updateColumn(index, 'values', values.map((v, i) => i === optIndex ? Object.assign({}, v, { [key]: value }) : v));
  };

  const inputStyle = 'width:100%;padding:4px 6px;border:1px solid #ccc;border-radius:2px;font-size:12px;box-sizing:border-box;';
  const labelStyle = 'display:block;font-size:11px;font-weight:600;margin-bottom:2px;color:#444;';

  return html`
    <div>
      ${cols.map((col, index) => html`
        <div key=${index} class="bio-properties-panel-collapsible-entry" style="margin-bottom:4px;">
          <div class="bio-properties-panel-collapsible-entry-header" style="display:flex;align-items:center;justify-content:space-between;">
            <div class="bio-properties-panel-collapsible-entry-header-title">Columna ${index + 1}</div>
            <button type="button"
              title="Delete item"
              class="bio-properties-panel-remove-entry"
              onClick=${() => removeColumn(index)}>
              <${DeleteIcon} />
            </button>
          </div>
          <div class="bio-properties-panel-collapsible-entry-entries open" style="padding:8px 20px 4px;">
            <div style="margin-bottom:6px;">
              <label style=${labelStyle}>Etiqueta</label>
              <input type="text" style=${inputStyle} value=${col.label || ''}
                onInput=${e => updateColumn(index, 'label', e.target.value)} />
            </div>
            <div style="margin-bottom:6px;">
              <label style=${labelStyle}>Clave (key)</label>
              <input type="text" style=${inputStyle} value=${col.key || ''}
                onInput=${e => updateColumn(index, 'key', e.target.value)} />
            </div>
            <div style="margin-bottom:6px;">
              <label style=${labelStyle}>Tipo</label>
              <select style=${inputStyle} value=${col.type || 'text'}
                onChange=${e => updateColumn(index, 'type', e.target.value)}>
                <option value="text">text</option>
                <option value="number">number</option>
                <option value="date">date</option>
                <option value="textarea">textarea</option>
                <option value="checkbox">checkbox</option>
                <option value="select">select</option>
              </select>
              ${col.type === 'checkbox' && cols.filter(c => c.type === 'checkbox').length > 1
                ? html`<span style="color:#a4262c;font-size:11px;">⚠ Solo se permite una columna de tipo checkbox</span>`
                : null}
            </div>
            <div style="display:flex;align-items:center;gap:6px;">
              <input type="checkbox" id=${'et-vis-' + field.id + '-' + index}
                checked=${col.visibleInTable !== false}
                onChange=${e => updateColumn(index, 'visibleInTable', e.target.checked)} />
              <label for=${'et-vis-' + field.id + '-' + index} style="font-size:12px;">Visible en tabla</label>
            </div>

            ${col.type === 'select' ? html`
              <div style="margin-top:10px;padding-top:8px;border-top:1px dashed #ddd;">
                <div style="display:flex;align-items:center;gap:6px;margin-bottom:6px;">
                  <input type="checkbox" id=${'et-cascada-' + field.id + '-' + index}
                    checked=${!!col.cascada}
                    onChange=${e => updateColumn(index, 'cascada', e.target.checked)} />
                  <label for=${'et-cascada-' + field.id + '-' + index} style="font-size:12px;">Cascada</label>
                </div>
                ${col.cascada ? html`
                  <div style="margin-bottom:6px;">
                    <label style=${labelStyle}>Llave cascada (key de la columna padre en esta misma tabla)</label>
                    <input type="text" style=${inputStyle} value=${col.llaveCascada || ''}
                      onInput=${e => updateColumn(index, 'llaveCascada', e.target.value)} />
                  </div>` : null}

                <label style=${labelStyle}>Opciones</label>
                ${(col.values || []).map((opt, optIndex) => html`
                  <div key=${optIndex} style="display:flex;gap:4px;margin-bottom:4px;align-items:center;">
                    <input type="text" placeholder="Etiqueta" style=${inputStyle} value=${opt.label || ''}
                      onInput=${e => updateColumnOption(index, optIndex, 'label', e.target.value)} />
                    <input type="text" placeholder="Valor" style=${inputStyle} value=${opt.value || ''}
                      onInput=${e => updateColumnOption(index, optIndex, 'value', e.target.value)} />
                    ${col.cascada ? html`
                      <input type="text" placeholder="Valor cascada" style=${inputStyle} value=${opt.valorCascada || ''}
                        onInput=${e => updateColumnOption(index, optIndex, 'valorCascada', e.target.value)} />` : null}
                    <button type="button" title="Eliminar opción" onClick=${() => removeColumnOption(index, optIndex)}
                      style="background:#a4262c;color:#fff;border:none;border-radius:3px;cursor:pointer;padding:4px 8px;font-size:11px;flex-shrink:0;">✕</button>
                  </div>`)}
                <button type="button" onClick=${() => addColumnOption(index)}
                  style="background:#0078d4;color:#fff;border:none;border-radius:3px;cursor:pointer;padding:4px 10px;font-size:11px;margin-top:2px;">
                  + Agregar opción
                </button>
              </div>` : null}
          </div>
        </div>
      `)}
      <div class="bio-properties-panel-group-header-buttons" style="margin-top:4px;">
        <button type="button"
          title="Create new list item"
          class="bio-properties-panel-group-header-button bio-properties-panel-add-entry"
          onClick=${addColumn}>
          <${CreateIcon} />
          ${cols.length === 0 ? html`<span class="bio-properties-panel-add-entry-label">Create</span>` : null}
        </button>
      </div>
    </div>`;
}

// ── fin Tabla Expandible ──────────────────────────────────────────────────────

export class CustomPropertiesProvider {
  constructor(propertiesPanel) {
    propertiesPanel.registerProvider(this, 500);
  }

  /**
   * Return the groups provided for the given field.
   *
   * @param {any} field
   * @param {function} editField
   *
   * @return {(Object[]) => (Object[])} groups middleware
   */
  getGroups(field, editField) {
    /**
     * We return a middleware that modifies
     * the existing groups.
     *
     * @param {Object[]} groups
     *
     * @return {Object[]} modified groups
     */
    return (groups) => {
      /*     if (field.type !== 'fileInput') {
        return groups;
      } */

      const generalGroup = groups.find((group) => group.id === "general");
      
      if (field.type == "viewerButton") {
        // Quitar todas las entradas por defecto (key, etc.) y poner solo las nuestras
        generalGroup.entries = [];
        generalGroup.entries.push(EtiquetaArchivoEntry(field, editField));
        generalGroup.entries.push(LlaveArchivoEntry(field, editField));
      }

      if (field.type === "select") {
        groups = groups.filter((group) => group.id !== "valuesSource");

        // Agregar propiedades de buscador y cascada al grupo general
        const selectGeneralGroup = groups.find((group) => group.id === "general");
        if (selectGeneralGroup) {
          selectGeneralGroup.entries.push(BuscadorCheckboxEntry(field, editField));
          selectGeneralGroup.entries.push(CascadaCheckboxEntry(field, editField));
          selectGeneralGroup.entries.push(LlaveCascadaEntry(field, editField));
        }

        // Inyectar campo "Valor cascada" en cada opción estática cuando cascada está activa
        if (field.cascada) {
          const staticOptionsGroup = groups.find((group) => group.id === "staticOptions");
          if (staticOptionsGroup && staticOptionsGroup.items) {
            staticOptionsGroup.items = staticOptionsGroup.items.map((item, index) => ({
              ...item,
              entries: [
                ...item.entries,
                ValorCascadaOptionEntry(editField, field, index),
              ],
            }));
          }
        }
      }
      //
      if (field.type === "textfield") {
        const generalGroup = groups.find((group) => group.id === "general");

        if (generalGroup) {
          generalGroup.entries.push(PasswordCheckboxEntry(field, editField));
          generalGroup.entries.push(AutocompleteCheckboxEntry(field, editField));
        }
      }

      if (field.type === "fileInput") {
        const generalGroup = groups.find((group) => group.id === "general");

        if (generalGroup) {
          generalGroup.entries.push(CaptureContentAsVariableEntry(field, editField));
          generalGroup.entries.push(VariableNameEntry(field, editField));
          generalGroup.entries.push(CaptureContentAsVariableEntryURL(field, editField));
          generalGroup.entries.push(VariableNameEntryURL(field, editField));
        }

        // Agregar grupo de validación con checkbox Requerido
        const validationGroupIndex = groups.findIndex((g) => g.id === 'validation');
        const validationEntries = [FileInputRequiredEntry(field, editField)];
        if (validationGroupIndex >= 0) {
          groups[validationGroupIndex].entries.push(...validationEntries);
        } else {
          groups.push({
            id: 'validation',
            label: 'Validación',
            entries: validationEntries,
          });
        }
      }

      if (field.type === "expandableTable") {
        // Ocultar grupos que no aplican
        groups = groups.filter(
          (group) => !["valuesSource", "validation"].includes(group.id)
        );

        // Agregar entradas al grupo general
        const generalGroupET = groups.find((g) => g.id === "general");
        if (generalGroupET) {
          [
            { id: 'et-defaultValue-'   + field.id, component: DefaultValueEntryET   },
            { id: 'et-allowAdd-'       + field.id, component: AllowAddEntry         },
            { id: 'et-allowEdit-'      + field.id, component: AllowEditEntry        },
            { id: 'et-allowCheckbox-'  + field.id, component: AllowCheckboxEntry    },
            { id: 'et-countKey-'       + field.id, component: CountKeyEntry         },
            { id: 'et-countCheckedKey-'+ field.id, component: CountCheckedKeyEntry  },
          ].forEach(e => generalGroupET.entries.push({ ...e, field, editField }));
        }

        groups.push({
          id: 'expandableTableColumns',
          label: 'Columnas de la tabla',
          entries: [
            {
              id: 'et-columns-' + field.id,
              component: ColumnsEditor,
              field,
              editField,
            },
          ],
        });
      }

      groups.forEach((group) => {
        group.label = translate(group.label);

        if (group.entries) {
          group.entries.forEach((entry) => {
            // Sobrescribe el componente para inyectar la traducción
            const OriginalComponent = entry.component;
            console.log(OriginalComponent);
            console.log(entry);

            /* entry.component = (props) => {
              // Modifica las props antes de pasarlas al componente original
              console.log(props);
           
              
              return OriginalComponent(props);
            }; */
          });
        }
      });

      return groups;
    };
  }
}

CustomPropertiesProvider.$inject = ["propertiesPanel"];

function Entries(field, editField) {
  const onChange = (key) => {
    return (value) => {
      editField(field, [key], value);
    };
  };

  const getValue = (key) => {
    return () => {
      return get(field, [key]);
    };
  };

  return [
    {
      id: "endpoint",
      component: Endpoint,
      getValue,
      field,
      isEdited: isTextFieldEntryEdited,
      onChange,
    },
    {
      id: "message",
      component: Message,
      getValue,
      field,
      isEdited: isFeelEntryEdited,
      onChange,
    },
  ];
}

function Endpoint(props) {
  const { field, getValue, id, onChange } = props;

  const debounce = (fn) => fn;

  return TextFieldEntry({
    debounce,
    element: field,
    getValue: getValue("endpoint"),
    id,
    label: "Endpoint",
    setValue: onChange("endpoint"),
  });
}

function Message(props) {
  const { field, getValue, id, onChange } = props;

  const debounce = (fn) => fn;

  const variables = useVariables().map((name) => ({ name }));

  return FeelTemplatingEntry({
    debounce,
    element: field,
    getValue: getValue("message"),
    id,
    label: "Message",
    setValue: onChange("message"),
    variables,
  });
}

/*
 * collect range entries for our custom group
 */
function translate(key) {
  const translations = {
    "Text field": "Campo de Texto",
    "Text area": "Área de texto",
    Number: "Número",
    "Date time": "Fecha",
    Expression: "Expresión",
    Components: "Componentes",
    Checkbox: "Casilla Verificacion",
    "Checkbox group": "Casillas Verificacion",
    "": "",
    Select: "Menú Desplegable",
    "Tag list": "Lista etiquetas",
    "Text view": "Texto",
    "Image view": "Imagen",
    Table: "Tabla",
    "HTML view": "Vista HTML",
    Spacer: "Espacio",
    Separator: "Separador",
    Group: "Grupo",
    "Dynamic list": "Lista Dinámica",
    Button: "Botón",
    Selection: "Selección",
    Presentation: "Presentación",
    Containers: "Contenedores",
    Action: "Acción",
    "Search components": "Buscar componentes",
    "Build your form": "Construye tu formulario",
    "Drag and drop components here to start designing.":
      "Arrastra y suelta componentes aquí para comenzar a diseñar.",
    "Use the preview window to test your form.":
      "Utiliza la ventana de vista previa para probar tu formulario.",
    Condition: "Condición",
    "Field label": "Etiqueta",
    "Field description": "Descripción",
    Key: "Llave",
    "Default value": "Valor por Defecto",
    "Hide if": "Esconder si",
    "Binds to a form variable": "Se une a una variable de formulario",
    "Condition under which the field is hidden":
      "Condición bajo la cual el campo está oculto.",
    Layout: "Disposición",
    Columns: "Columnas",
    Appearance: "Apariencia",
    Prefix: "Prefijo",
    Suffix: "Sufijo",
    Serialization: "Serialización",
    "Output as string": "Salida a texto",
    "Allows arbitrary precision values":
      "Permite valores de precisión arbitrarios.",
    Validation: "Validación",
    Required: "Requerido",
    Minimum: "Mínimo",
    Maximum: "Máximo",
    "Custom properties": "Propiedades personalizadas",
    "Create a Text field element": "Crear Campo de Texto",
    "Create a Text area element": "Crear Area de Texto",
    "Create a Number element": "Crear Campo Numérico",
    "Create a Date time element": "Crear Campo de Fecha",
    "Create a Expression element": "Crear Campo de Expresión",
    "Create a Checkbox element": "Crear Campo Casilla de Verificación",
    "Create a Checkbox group element":
      "Crear Campo grupo Casilla de Verificación",
    "Create a Radio group element": "Crear Campo Radio Group",
    "Create a Select element": "Crear Campo Desplegable",
    "Create a Tag list element": "Crear Campo Lista etiquetas",
    "Create a Text view element": "Crear Visualizador Texto",
    "Create an Image view element": "Crear Visualizador de imagenes",
    "Create a Table element": "Crear Tabla",
    "Create a HTML view element": "Crear Visualizador de HTML",
    "Create a Spacer element": "Crear Espaciador",
    "Create a Separator element": "Crear Separador",
    "Create a Group element": "Crear Campo Grupo",
    "Create a Dynamic list element": "Crear Lista Dinámica",
    "Create a iFrame element": "Crear IFrame",
    "Create a Button element": "Crear Botón",
    "Create a Expandable table element": "Crear Tabla Expandible",
    "Expandable table": "Tabla Expandible",
    Constraints: "Restricciones",
    "Disallow past dates": "No permitir fechas pasadas",
    "Static options": "Opciones estáticas",
    "Options source": "Origen de las opciones",
    Value: "Valor",
    Type: "Tipo",
    '"List of items" defines a constant, predefined set of form options.':
      '"List of items" defines a constant, predefined set of form options.',
  };

  return translations[key] || key; // Devuelve la traducción o la clave si no se encuentra
}
